import moment from 'moment';
import { IApiError } from '../../../models/api';
import { ApiService } from '../../../services/api';
import { getErrorMessages } from '../../../utils/formatters';
import {
  extractFilenameFromContentDisposition,
  generateAsyncActionNames,
} from '../../../utils/helpers';
import { AppDispatch } from '../../type';

const { fetchReports } = ApiService;

enum ReportsActionsEnum {
  DOWNLOAD_ORDERS_REPORT = 'DOWNLOAD_ORDERS_REPORT',
  DOWNLOAD_ORDERS_BY_SITES_REPORT = 'DOWNLOAD_ORDERS_BY_SITES_REPORT',
  DOWNLOAD_SITE_DETAILS_REPORT = 'DOWNLOAD_SITE_DETAILS_REPORT',
  DOWNLOAD_WAITING_LIST_REPORT = 'DOWNLOAD_WAITING_LIST_REPORT',
  DOWNLOAD_CELLS_REPORT = 'DOWNLOAD_CELLS_REPORT',
}

export const ReportsActionsNames = {
  [ReportsActionsEnum.DOWNLOAD_ORDERS_REPORT]: generateAsyncActionNames(
    ReportsActionsEnum.DOWNLOAD_ORDERS_REPORT
  ),
  [ReportsActionsEnum.DOWNLOAD_ORDERS_BY_SITES_REPORT]:
    generateAsyncActionNames(
      ReportsActionsEnum.DOWNLOAD_ORDERS_BY_SITES_REPORT
    ),
  [ReportsActionsEnum.DOWNLOAD_SITE_DETAILS_REPORT]: generateAsyncActionNames(
    ReportsActionsEnum.DOWNLOAD_SITE_DETAILS_REPORT
  ),
  [ReportsActionsEnum.DOWNLOAD_WAITING_LIST_REPORT]: generateAsyncActionNames(
    ReportsActionsEnum.DOWNLOAD_WAITING_LIST_REPORT
  ),
  [ReportsActionsEnum.DOWNLOAD_CELLS_REPORT]: generateAsyncActionNames(
    ReportsActionsEnum.DOWNLOAD_CELLS_REPORT
  ),
};

const Actions = {
  [ReportsActionsEnum.DOWNLOAD_ORDERS_REPORT]: {
    START: () => ({
      type: ReportsActionsNames.DOWNLOAD_ORDERS_REPORT.START,
    }),
    FULFILLED: () => ({
      type: ReportsActionsNames.DOWNLOAD_ORDERS_REPORT.FULFILLED,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: ReportsActionsNames.DOWNLOAD_ORDERS_REPORT.REJECTED,
      payload: error,
    }),
  },
  [ReportsActionsEnum.DOWNLOAD_ORDERS_BY_SITES_REPORT]: {
    START: () => ({
      type: ReportsActionsNames.DOWNLOAD_ORDERS_BY_SITES_REPORT.START,
    }),
    FULFILLED: () => ({
      type: ReportsActionsNames.DOWNLOAD_ORDERS_BY_SITES_REPORT.FULFILLED,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: ReportsActionsNames.DOWNLOAD_ORDERS_BY_SITES_REPORT.REJECTED,
      payload: error,
    }),
  },
  [ReportsActionsEnum.DOWNLOAD_SITE_DETAILS_REPORT]: {
    START: () => ({
      type: ReportsActionsNames.DOWNLOAD_SITE_DETAILS_REPORT.START,
    }),
    FULFILLED: () => ({
      type: ReportsActionsNames.DOWNLOAD_SITE_DETAILS_REPORT.FULFILLED,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: ReportsActionsNames.DOWNLOAD_SITE_DETAILS_REPORT.REJECTED,
      payload: error,
    }),
  },
  [ReportsActionsEnum.DOWNLOAD_WAITING_LIST_REPORT]: {
    START: () => ({
      type: ReportsActionsNames.DOWNLOAD_WAITING_LIST_REPORT.START,
    }),
    FULFILLED: () => ({
      type: ReportsActionsNames.DOWNLOAD_WAITING_LIST_REPORT.FULFILLED,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: ReportsActionsNames.DOWNLOAD_WAITING_LIST_REPORT.REJECTED,
      payload: error,
    }),
  },
  [ReportsActionsEnum.DOWNLOAD_CELLS_REPORT]: {
    START: () => ({
      type: ReportsActionsNames.DOWNLOAD_CELLS_REPORT.START,
    }),
    FULFILLED: () => ({
      type: ReportsActionsNames.DOWNLOAD_CELLS_REPORT.FULFILLED,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: ReportsActionsNames.DOWNLOAD_CELLS_REPORT.REJECTED,
      payload: error,
    }),
  },
};

const downloadOrdersReport =
  (fromDate: Date, toDate: Date) => async (dispatch: AppDispatch) => {
    dispatch(Actions[ReportsActionsEnum.DOWNLOAD_ORDERS_REPORT].START());
    try {
      const response = await fetchReports(
        `Order/GetExcelOrdersReport?FromDate=${fromDate}&ToDate=${toDate}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const contentDisposition = response.headers.get('Content-Disposition');
      if (!contentDisposition) return;
      const fileName =
        extractFilenameFromContentDisposition(contentDisposition);
      const blob = await response.blob();
      if (blob.type !== 'application/json' && fileName) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName.name;
        a.click();
      }

      dispatch(Actions[ReportsActionsEnum.DOWNLOAD_ORDERS_REPORT].FULFILLED());
    } catch (error: any) {
      dispatch(
        Actions[ReportsActionsEnum.DOWNLOAD_ORDERS_REPORT].REJECTED(
          getErrorMessages(error)
        )
      );
    }
  };

const downloadOrdersBySitesReport =
  (fromDate: Date, toDate: Date) => async (dispatch: AppDispatch) => {
    dispatch(
      Actions[ReportsActionsEnum.DOWNLOAD_ORDERS_BY_SITES_REPORT].START()
    );
    try {
      const response = await fetchReports(
        `Site/GetExcelSitesOrdersConcentrationReport?FromDate=${fromDate}&ToDate=${toDate}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      if (!contentDisposition) return;
      const fileName =
        extractFilenameFromContentDisposition(contentDisposition);
      const blob = await response.blob();

      if (blob.type !== 'application/json' && fileName) {
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = fileName.name;
        a.click();
      }

      dispatch(
        Actions[ReportsActionsEnum.DOWNLOAD_ORDERS_BY_SITES_REPORT].FULFILLED()
      );
    } catch (error: any) {
      dispatch(
        Actions[ReportsActionsEnum.DOWNLOAD_ORDERS_BY_SITES_REPORT].REJECTED(
          getErrorMessages(error)
        )
      );
    }
  };

const downloadSiteDetailsReport =
  (siteId: number) => async (dispatch: AppDispatch) => {
    dispatch(Actions[ReportsActionsEnum.DOWNLOAD_SITE_DETAILS_REPORT].START());
    try {
      const response = await fetchReports(
        `Site/GetPDFSiteReport?SiteId=${siteId}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const contentDisposition = response.headers.get('Content-Disposition');
      if (!contentDisposition) return;
      const fileName =
        extractFilenameFromContentDisposition(contentDisposition);

      const blob = await response.blob();
      if (blob.type !== 'application/json' && fileName) {
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `${decodeURIComponent(fileName.name)} ${moment(new Date()).format('DD-MM-YYYY')}`;
        a.click();
      }

      dispatch(
        Actions[ReportsActionsEnum.DOWNLOAD_SITE_DETAILS_REPORT].FULFILLED()
      );
    } catch (error: any) {
      dispatch(
        Actions[ReportsActionsEnum.DOWNLOAD_SITE_DETAILS_REPORT].REJECTED(
          getErrorMessages(error)
        )
      );
    }
  };

const downloadWaitingListReport =
  (params: {
    fromCreatedDate?: Date | null;
    toCreatedDate?: Date | null;
    siteIds?: number[];
    compoundIds?: number[];
    waitingListTypeIds?: number[];
    waitingListStatusIds?: number[];
  }) =>
  async (dispatch: AppDispatch) => {
    dispatch(Actions[ReportsActionsEnum.DOWNLOAD_WAITING_LIST_REPORT].START());
    try {
      const query = new URLSearchParams();
      if (params.fromCreatedDate)
        query.append('FromDate', String(params.fromCreatedDate));
      if (params.toCreatedDate)
        query.append('ToDate', String(params.toCreatedDate));
      params.siteIds?.forEach((id) => query.append('SiteIds', String(id)));
      params.compoundIds?.forEach((id) =>
        query.append('CompoundIds', String(id))
      );
      params.waitingListTypeIds?.forEach((id) =>
        query.append('WaitingListTypes', String(id))
      );
      params.waitingListStatusIds?.forEach((id) =>
        query.append('WaitingListStatuses', String(id))
      );

      // Placeholder endpoint name - replace with actual when provided by backend
      const response = await fetchReports(
        `WaitingList/GetExcelWaitingListReport?${query.toString()}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      if (!contentDisposition) return;
      const fileName =
        extractFilenameFromContentDisposition(contentDisposition);
      const blob = await response.blob();
      if (blob.type !== 'application/json' && fileName) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName.name;
        a.click();
      }

      dispatch(
        Actions[ReportsActionsEnum.DOWNLOAD_WAITING_LIST_REPORT].FULFILLED()
      );
    } catch (error: any) {
      dispatch(
        Actions[ReportsActionsEnum.DOWNLOAD_WAITING_LIST_REPORT].REJECTED(
          getErrorMessages(error)
        )
      );
    }
  };

// TODO backend-confirm: verify exact query parameter keys if they differ from GetCells API
// Filters mimic getCells API (example assumed params: SiteIds, CompoundIds, ClosetNumbers, CellStatuses, FromCreatedDate, ToCreatedDate)
const downloadCellsReport =
  (params: {
    siteIds?: string[] | number[];
    compoundIds?: string[] | number[];
    cellId?: string | number;
    cellNumber?: string | number;
    cellNickName?: string;
    closetNumber?: string | number;
    lockCaseId?: string | number;
    cellStatus?: number | null;
    fromPairingDate?: Date | null;
    toPairingDate?: Date | null;
  }) =>
  async (dispatch: AppDispatch) => {
    dispatch(Actions[ReportsActionsEnum.DOWNLOAD_CELLS_REPORT].START());
    try {
      const query = new URLSearchParams();
      params.siteIds?.forEach((id) => query.append('SiteIds', String(id)));
      params.compoundIds?.forEach((id) =>
        query.append('CompoundIds', String(id))
      );
      if (params.cellId) query.append('CellId', String(params.cellId));
      if (params.cellNumber)
        query.append('CellNumber', String(params.cellNumber));
      if (params.cellNickName)
        query.append('CellNickName', String(params.cellNickName));
      if (params.closetNumber)
        query.append('ClosetIds', String(params.closetNumber));
      if (params.lockCaseId)
        query.append('LockCaseIds', String(params.lockCaseId));
      if (params.cellStatus != null)
        query.append('CellStatuses', String(params.cellStatus));
      if (params.fromPairingDate)
        query.append('FromPairingDate', String(params.fromPairingDate));
      if (params.toPairingDate)
        query.append('ToPairingDate', String(params.toPairingDate));

      const response = await fetchReports(
        `Cell/GetExcelCellsReport?${query.toString()}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const contentDisposition = response.headers.get('Content-Disposition');
      if (!contentDisposition) return;
      const fileName =
        extractFilenameFromContentDisposition(contentDisposition);
      const blob = await response.blob();
      if (blob.type !== 'application/json' && fileName) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName.name;
        a.click();
      }
      dispatch(Actions[ReportsActionsEnum.DOWNLOAD_CELLS_REPORT].FULFILLED());
    } catch (error: any) {
      dispatch(
        Actions[ReportsActionsEnum.DOWNLOAD_CELLS_REPORT].REJECTED(
          getErrorMessages(error)
        )
      );
    }
  };

export const ReportsActions = {
  downloadOrdersReport,
  downloadOrdersBySitesReport,
  downloadSiteDetailsReport,
  downloadWaitingListReport,
  downloadCellsReport,
};
